import { useEffect, useMemo, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import { useRequestAnalyticsQuery } from "@/hooks/useRequestAnalyticsQuery";
import i18n from "@/i18n/i18n";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import { AnalyticsCardData, DateRange } from "@/services/StorePerformanceService";
import { Store } from "@/types/store";
import {
  CurrencyCode,
  formatCurrencyWithNoDecimals,
  getStoreCurrencyIdentifiers,
} from "@/utils/currency";
import { formatPercentFromWhole } from "@/utils/number";

export const useCanceledRequests = (
  stores: Store[] | null,
  dateRange?: DateRange,
  deviceType?: DeviceType,
  areaIds?: number[],
): AnalyticsCardData => {
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const [rawData, setRawData] = useState<{
    percent: number;
    canceledRequests: number;
    totalRequests: number;
    totalRequestValue: number;
  } | null>(null);
  const { baseParams, beginRequest, requestKey } = useRequestAnalyticsQuery({
    stores,
    dateRange,
    deviceType,
    areaIds,
  });
  const [currencyCode, currencySymbol] = getStoreCurrencyIdentifiers(stores);

  useEffect(() => {
    const isCurrent = beginRequest();
    if (!baseParams || !requestKey) {
      setRawData(null);
      return;
    }

    const fetchCanceledRequests = async () => {
      try {
        const analyticsService = AnalyticsServiceV2.getInstance();
        const totalResponse = await analyticsService.getRequestAnalytics(baseParams);
        const totalRequests = totalResponse?.data?.totalRequests ?? 0;

        if (totalRequests <= 0) {
          if (isCurrent()) setRawData(null);
          return;
        }

        const canceledResponse = await analyticsService.getRequestAnalytics({
          ...baseParams,
          requestStatus: "CANCELED",
        });
        const canceledRequests = canceledResponse?.data?.totalRequests ?? 0;

        const percent = (canceledRequests / totalRequests) * 100;
        if (!isCurrent()) return;
        setRawData({
          percent,
          canceledRequests,
          totalRequests,
          totalRequestValue: Math.round(canceledResponse.data.totalRequestValue),
        });
      } catch (error) {
        console.error("Error fetching canceled requests:", error);
        if (isCurrent()) setRawData(null);
      }
    };

    void fetchCanceledRequests();
  }, [baseParams, beginRequest, requestKey]);

  return useMemo(() => {
    if (!rawData) {
      return {
        previousValue: null,
        isPositive: true,
        pillText: "",
        subtitle: i18n.t("common.analytics.noDataForTimeframe"),
        value: "--",
        rawValue: null,
      };
    }

    const value = formatCurrencyWithNoDecimals(
      rawData.totalRequestValue,
      currencyCode as CurrencyCode,
      currencySymbol,
    );

    return {
      previousValue: null,
      value: formatPercentFromWhole(rawData.percent, currentLanguage),
      subtitle: i18n.t("common.analytics.itemRequestsValue", { value }),
      isPositive: rawData.canceledRequests === 0,
      pillText: i18n.t("common.analytics.requestsProgress", {
        current: rawData.canceledRequests,
        total: rawData.totalRequests,
      }),
      rawValue: rawData.percent,
    };
  }, [rawData, currentLanguage, currencyCode, currencySymbol]);
};
